import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem, Autocomplete, createFilterOptions,
  FormControlLabel, Checkbox,
} from '@mui/material';
import ComposerMailDialog from './ComposerMailDialog';
import { ETATS_VISA } from './EtatVisaSelect';
import { recupererPaysListe } from '../Services';
import { ModeleProposable, messageErreurs } from '../services/formulairesServices';
import { useApiFormulaires } from '../formulaires/ApiFormulairesContext';

// « Nouveau dossier » (création manuelle), partagé par l'admin (ListesDemandes) et l'admin Legrand (ListeDemandesLegrand) :
// champs du dossier + « Formulaire à joindre » facultatif. Après la création : instance jointe puis, si « Envoyer le
// formulaire au client à la création » est cochée (défaut), composeur du mail « formulaire à remplir » ; décochée,
// l'instance reste au statut « créé » sans mail. Chaque échec est signalé sans annuler la création du dossier (le
// formulaire reste joignable/envoyable depuis le panneau Informations du formulaire).
// Le parent fournit la création (creerDossier : admin ou Legrand) et, s'il le faut, un contrôle d'e-mail ; les appels
// formulaires viennent d'ApiFormulairesContext. Le composeur reste monté après la fermeture du dialog.

export interface DonneesNouveauDossier {
  email: string;
  pays: string;
  validite: string;
  dateDemande: string;
  nom: string;
  prenom: string;
  dateDepart: string;
  etatVisa: string;
}

interface NouveauDossierDialogProps {
  open: boolean;
  onClose: () => void;
  creerDossier: (d: DonneesNouveauDossier) => Promise<{ id: number; chemin: string }>;
  onCree: () => void | Promise<void>;          // après création (rechargement de la liste)
  erreurEmail?: (email: string) => string | null; // message si l'e-mail saisi est refusé, null sinon
  aideEmail?: string;                           // texte sous le champ e-mail
}

interface Composeur { idInstance: number; destinataire: string; sujet: string; corpsHtml: string }

const filtrerPays = createFilterOptions<string>();
// Même pays à la casse et aux accents près (« inde » = « Inde », « Etats-Unis » = « États-Unis »)
const memePays = (a: string, b: string) => a.localeCompare(b, 'fr', { sensitivity: 'base' }) === 0;

const NouveauDossierDialog: React.FC<NouveauDossierDialogProps> = ({ open, onClose, creerDossier, onCree, erreurEmail, aideEmail }) => {
  const api = useApiFormulaires();
  const [paysOptions, setPaysOptions] = useState<string[]>([]);
  const paysCharges = useRef(false); // liste non vide reçue : plus de rechargement
  const [ndEmail, setNdEmail] = useState('');
  const [ndPays, setNdPays] = useState('');
  const [ndValidite, setNdValidite] = useState('');
  const [ndDateDemande, setNdDateDemande] = useState('');
  const [ndNom, setNdNom] = useState('');
  const [ndPrenom, setNdPrenom] = useState('');
  const [ndDateDepart, setNdDateDepart] = useState('');
  const [ndEtat, setNdEtat] = useState('à traiter');
  const [ndSaving, setNdSaving] = useState(false);
  // Formulaire à joindre : modèles actifs chargés à chaque ouverture du dialog
  const [ndModeles, setNdModeles] = useState<ModeleProposable[]>([]);
  const [ndIdModele, setNdIdModele] = useState('');
  const [ndEnvoyer, setNdEnvoyer] = useState(true); // ouvrir le composeur après la création (sinon instance « créé » sans mail)
  // Composeur ouvert après la création (état séparé des nd*, non remis à zéro par le reset du dialog)
  const [composeur, setComposeur] = useState<Composeur | null>(null);

  // Chargements à l'ouverture : pays (jusqu'à une liste non vide), modèles actifs (1 GET léger) ; 503/erreur → champ
  // masqué, retenté à la prochaine ouverture. La sélection précédente n'est gardée que si le modèle est toujours proposé
  useEffect(() => {
    if (!open) return;
    if (!paysCharges.current) {
      recupererPaysListe().then((l) => { setPaysOptions(l); paysCharges.current = l.length > 0; });
    }
    api.listerModelesActifs()
      .then((l) => { setNdModeles(l); setNdIdModele((v) => (l.some((m) => String(m.id) === v) ? v : '')); })
      .catch(() => { setNdModeles([]); setNdIdModele(''); });
  }, [open, api]);

  const messageEmail = erreurEmail && ndEmail.trim() ? erreurEmail(ndEmail.trim()) : null;
  // Pays de la liste correspondant à la saisie (orthographe de la table pays), undefined si hors liste
  const paysListe = (s: string) => paysOptions.find((p) => memePays(p, s.trim()));
  const paysHorsListe = !!ndPays.trim() && !paysListe(ndPays);

  const handleCreerDossier = async () => {
    if (!ndEmail.trim() || !ndNom.trim() || !ndPrenom.trim() || !ndPays.trim() || messageEmail) return;
    setNdSaving(true);
    try {
      const res = await creerDossier({
        email: ndEmail.trim(),
        pays: paysListe(ndPays) || ndPays.trim(),
        validite: ndValidite.trim(),
        dateDemande: ndDateDemande,
        nom: ndNom.trim(),
        prenom: ndPrenom.trim(),
        dateDepart: ndDateDepart,
        etatVisa: ndEtat
      });
      onClose();
      setNdEmail('');
      setNdPays('');
      setNdValidite('');
      setNdDateDemande('');
      setNdNom('');
      setNdPrenom('');
      setNdDateDepart('');
      setNdEtat('à traiter');
      const idModele = ndIdModele;
      const envoyer = ndEnvoyer;
      setNdIdModele('');
      setNdEnvoyer(true);
      if (idModele) {
        let inst: { id: number } | null = null;
        try {
          inst = await api.creerInstance({ chemin: res.chemin, id_modele: Number(idModele) });
        } catch (e2) {
          console.error('Erreur formulaire joint', e2);
          window.alert("Dossier créé, mais le formulaire n'a pas pu être joint : " + messageErreurs(e2, 'erreur inconnue') + ". Vous pouvez le joindre depuis Informations du formulaire.");
        }
        if (inst && envoyer) {
          try {
            const ap = await api.apercuMailInstance(inst.id, false);
            setComposeur({ idInstance: inst.id, destinataire: ap.destinataire, sujet: ap.sujet, corpsHtml: ap.corps_html });
          } catch (e3) {
            console.error('Erreur preparation mail', e3);
            window.alert("Formulaire joint (statut créé) mais le mail n'a pas pu être préparé : " + messageErreurs(e3, 'erreur inconnue') + ". Envoyez-le depuis Informations du formulaire.");
          }
        }
      }
      await onCree();
    } catch (e) {
      console.error('Erreur création dossier', e);
      // Message du backend s'il en donne un (Legrand : e-mail hors domaine, droits) ; l'admin répond { error } → défaut
      window.alert(messageErreurs(e, 'Erreur lors de la création du dossier.'));
    } finally {
      setNdSaving(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Nouveau dossier</DialogTitle>
        <DialogContent>
          <TextField
            label="Email du client"
            type="email"
            fullWidth
            required
            value={ndEmail}
            onChange={(e) => setNdEmail(e.target.value)}
            error={!!messageEmail}
            helperText={messageEmail || aideEmail}
            style={{ marginTop: 10 }}
          />
          <div style={{ display: 'flex', gap: 10 }}>
            <TextField
              label="Nom"
              fullWidth
              required
              value={ndNom}
              onChange={(e) => setNdNom(e.target.value)}
              style={{ marginTop: 16 }}
            />
            <TextField
              label="Prénom"
              fullWidth
              required
              value={ndPrenom}
              onChange={(e) => setNdPrenom(e.target.value)}
              style={{ marginTop: 16 }}
            />
          </div>
          {/* Pays : liste de la table pays ou libellé libre, enregistré sur le dossier seulement (la table pays n'est
              jamais modifiée) ; une saisie hors liste est proposée en dernière option « Utiliser « … » (hors liste) » */}
          <Autocomplete
            freeSolo
            forcePopupIcon
            options={paysOptions}
            value={ndPays}
            filterOptions={(options, params) => {
              const filtres = filtrerPays(options, params);
              const saisie = params.inputValue.trim();
              return saisie && !paysListe(saisie) ? [...filtres, saisie] : filtres;
            }}
            renderOption={(props, p) => (
              <li {...props} key={p}>{paysListe(p) ? p : `Utiliser « ${p} » (hors liste)`}</li>
            )}
            onChange={(_, v) => setNdPays(v || '')}
            onInputChange={(_, v) => setNdPays(v)}
            renderInput={(params) => (
              <TextField {...params} label="Pays" required style={{ marginTop: 16 }}
                helperText={paysHorsListe
                  ? 'Pays hors liste : enregistré sur ce dossier seulement, la liste des pays reste inchangée'
                  : 'Choisissez dans la liste ou tapez un pays qui n\'y est pas'} />
            )}
          />
          <TextField
            label="Validité (visa demandé)"
            fullWidth
            value={ndValidite}
            onChange={(e) => setNdValidite(e.target.value)}
            style={{ marginTop: 16 }}
          />
          <div style={{ display: 'flex', gap: 10 }}>
            <TextField
              label="Date de la demande"
              type="date"
              fullWidth
              value={ndDateDemande}
              onChange={(e) => setNdDateDemande(e.target.value)}
              InputLabelProps={{ shrink: true }}
              style={{ marginTop: 16 }}
            />
            <TextField
              label="Date de départ"
              type="date"
              fullWidth
              value={ndDateDepart}
              onChange={(e) => setNdDateDepart(e.target.value)}
              InputLabelProps={{ shrink: true }}
              style={{ marginTop: 16 }}
            />
          </div>
          <TextField
            select
            label="État du dossier"
            fullWidth
            value={ndEtat}
            onChange={(e) => setNdEtat(e.target.value)}
            style={{ marginTop: 16 }}
          >
            {ETATS_VISA.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </TextField>
          {ndModeles.length > 0 && (
            <TextField select label="Formulaire à joindre (facultatif)" fullWidth value={ndIdModele}
              onChange={(e) => setNdIdModele(e.target.value)} style={{ marginTop: 16 }}
              helperText={ndIdModele && !ndEnvoyer
                ? 'Formulaire joint sans mail : à envoyer plus tard depuis Informations du formulaire'
                : 'Le mail au client sera à valider dans le composeur après la création'}>
              <MenuItem value="">Aucun</MenuItem>
              {ndModeles.map((m) => (<MenuItem key={m.id} value={String(m.id)}>{m.nom}</MenuItem>))}
            </TextField>
          )}
          {ndModeles.length > 0 && ndIdModele && (
            <FormControlLabel
              control={<Checkbox checked={ndEnvoyer} onChange={(e) => setNdEnvoyer(e.target.checked)} />}
              label="Envoyer le formulaire au client à la création"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Annuler</Button>
          <Button
            variant="contained"
            onClick={handleCreerDossier}
            disabled={
              ndSaving || !ndEmail.trim() || !ndNom.trim() || !ndPrenom.trim() || !ndPays.trim() || !!messageEmail
            }
          >
            Créer le dossier
          </Button>
        </DialogActions>
      </Dialog>

      {/* Composeur du mail « formulaire à remplir » (ouvert après la création d'un dossier avec formulaire joint) ;
          le rejet d'envoyerInstance (400/409/502) est attrapé dans ComposerMailDialog ; fermer sans envoyer laisse
          l'instance au statut « créé », envoyable depuis le panneau Informations du formulaire */}
      <ComposerMailDialog
        open={!!composeur} titre="Envoyer le formulaire au client"
        destinataire={composeur?.destinataire ?? ''} sujet={composeur?.sujet ?? ''} corpsHtml={composeur?.corpsHtml ?? ''}
        onEnvoyer={async (p) => { const r = await api.envoyerInstance(composeur!.idInstance, p); return r.mail; }}
        onClose={() => setComposeur(null)}
      />
    </>
  );
};

export default NouveauDossierDialog;
